// Tab Refund của hồ sơ khách (Mint-CustomerDetail › REFUND CORE). Số liệu là dữ liệu mẫu của board, sinh bằng cùng bộ
// sinh số giả ngẫu nhiên để dòng + thứ tự khớp board. Nguồn LOGI: OpsCostData TypeCost 'Refund' (COM = phía khách, Partner = ShortName).
export type RfCode = 'KB' | 'COM';
export type RfStatus = 'pend' | 'wait' | 'appr' | 'rq' | 'paid' | 'nil';
export type RfDbn = 'Done' | 'Partial' | 'Pending';
export type RfBucket = 'pend' | 'wait' | 'chi' | 'paid' | 'nil';

export interface RfRow {
  id: string;
  code: RfCode;
  job: string;
  mi: number;
  per: string;
  day: number;
  date: string;
  partner: string;
  cus: string;
  sale: string;
  vol: number;
  ct: string;
  usd: number;
  roe: number;
  collab: string;
  dbn: RfDbn;
  st: RfStatus;
  gk: string;
  doc?: string;
  /** tuyến (chỉ dòng KB) */
  pol?: string;
  pod?: string;
}
export interface RfDoc { doc: string; code: RfCode; partner: string; collab: string; mi: number; per: string; date: string; st: RfStatus; tpl: 'tax' | 'roe' }

export const RF_PERIODS = [['08', 'Aug'], ['09', 'Sep'], ['10', 'Oct']] as const;
export const RF_ST: Record<RfStatus, { label: string; dot: string }> = {
  pend: { label: 'Chờ gom', dot: 'var(--d-amber)' },
  wait: { label: 'Chờ duyệt', dot: 'var(--d-blue)' },
  appr: { label: 'Đã duyệt', dot: 'var(--d-violet)' },
  rq: { label: 'Chờ chi', dot: 'var(--d-violet)' },
  paid: { label: 'Đã chi', dot: 'var(--d-green)' },
  nil: { label: 'Không refund', dot: 'var(--d-gray)' },
};
export const RF_DBN: Record<RfDbn, { label: string; dot: string }> = {
  Done: { label: 'Khách đã trả DN', dot: 'var(--d-green)' },
  Partial: { label: 'Khách trả một phần DN', dot: 'var(--d-amber)' },
  Pending: { label: 'Khách chưa trả DN', dot: 'var(--d-gray)' },
};
export const RF_TPL = { tax: 'Tax 20%', roe: 'ROE 20.000' } as const;
export const bucketOf = (s: RfStatus): RfBucket => (s === 'appr' || s === 'rq' ? 'chi' : s);

const MON: [string, string, number, number][] = [['08', 'Aug', 31, 26310], ['09', 'Sep', 30, 26355], ['10', 'Oct', 22, 26402]];
const KBP: [string, number, [string, string][]][] = [
  ['ONE-LINE', 9, [['VNCLI', 'USLAX'], ['VNCLI', 'USLGB'], ['VNCMT', 'USNYC']]], ['WANHAI', 6, [['VNCLI', 'JPTYO'], ['VNCLI', 'KRPUS']]],
  ['YANGMING', 5, [['VNCLI', 'USLAX'], ['VNCLI', 'TWKHH']]], ['EVERGREEN', 4, [['VNCMT', 'NLRTM'], ['VNCMT', 'DEHAM']]], ['KMTC', 3, [['VNCLI', 'KRPUS']]],
  ['SITC-NEW', 3, [['VNCLI', 'CNSHA'], ['VNCLI', 'JPOSA']]], ['CMA-CGM', 2, [['VNCMT', 'AUSYD']]], ['HYUNDAI', 2, [['VNCLI', 'KRPUS']]],
];
const COMP: [string, number, string][] = [['NAMMY', 7, 'TUẤN'], ['GREENBUD', 6, 'DAVID'], ['LINDA ASIA', 4, 'LINH'], ['ARRIVE', 4, 'TUẤN'], ['NEWWIND', 3, 'LINH'], ['RUITONGVN-SC', 3, 'DAVID'], ['SAVITA', 2, 'DAVID'], ['COCOGROUP', 2, 'LINH']];
const OWNER: Record<string, string> = Object.fromEntries(COMP.map(([c, , s]) => [c, s]));

function build(): { rows: RfRow[]; docs: RfDoc[]; miss: { code: RfCode; cus: string; car: string; mi: number }[] } {
  let seed = 20261022;
  const r = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
  const pickW = <T extends [string, number, ...unknown[]]>(arr: T[]): T => {
    const tot = arr.reduce((a, x) => a + x[1], 0);
    let v = r() * tot;
    for (const x of arr) { v -= x[1]; if (v < 0) return x; }
    return arr[0]!;
  };
  const pick = <T,>(arr: T[]): T => arr[Math.floor(r() * arr.length) % arr.length]!;
  const CT = ['40HC', '40HC', '20DC', '40HC', '40RF'];
  const rows: RfRow[] = [];
  let seq = 0;
  const grp = (mi: number, pi: number): RfStatus => (mi === 0 ? (pi % 5 === 4 ? 'rq' : 'paid') : mi === 1 ? (['paid', 'rq', 'appr', 'wait'] as const)[pi % 4]! : 'wait');
  MON.forEach(([mm, mk, md, roe], mi) => {
    (['KB', 'COM'] as const).forEach((code) => {
      const n = code === 'KB' ? 26 : 20;
      for (let i = 0; i < n; i++) {
        const day = 1 + Math.floor(r() * md);
        let partner: string, cus: string, sale: string, pi: number, collab: string, lane: [string, string] | null = null;
        if (code === 'KB') {
          const p = pickW(KBP); pi = KBP.indexOf(p); partner = p[0];
          const li = Math.floor(r() * p[2].length) % p[2].length;
          lane = p[2][li]!;
          const c = pickW(COMP); cus = c[0]; sale = OWNER[cus]!; collab = 'CTV ' + (li % 2 ? 'B' : 'A');
        } else {
          const p = pickW(COMP); pi = COMP.indexOf(p); partner = p[0]; cus = p[0]; sale = p[2];
          pick(KBP); collab = 'CTV ' + (r() < 0.25 ? 'B' : 'A');
        }
        const vol = 1 + Math.floor(r() * 4), ct = pick(CT);
        const usd = code === 'KB' ? pick([50, 50, 50, 100, 25, 30, 20]) : pick([10, 20, 25, 30, 50, 5]);
        const nil = r() < 0.05;
        const dr = r();
        const dbn: RfDbn = mi === 0 ? 'Done' : mi === 1 ? (dr < 0.7 ? 'Done' : dr < 0.85 ? 'Partial' : 'Pending') : dr < 0.3 ? 'Done' : dr < 0.45 ? 'Partial' : 'Pending';
        const g = mi === 0 ? true : mi === 1 ? r() < 0.8 : r() < 0.5 && pi < 2;
        const noRoe = mi === 2 && !g && r() < 0.1;
        seq++;
        const job = 'SE2606' + String(36800 + mi * 600 + seq * 7).padStart(5, '0');
        rows.push({
          id: 'r' + seq, code, job, mi, per: mm, day, date: String(day).padStart(2, '0') + '/' + mk + '/2026', partner, cus, sale, vol, ct,
          usd: nil ? 0 : usd, roe: noRoe ? 0 : roe, collab: nil ? 'NIL' : collab, dbn, st: nil ? 'nil' : g ? grp(mi, pi) : 'pend', gk: g && !nil ? `${code}|${partner}|${collab}|${mi}` : '',
          ...(lane ? { pol: lane[0], pod: lane[1] } : {}),
        });
      }
    });
  });
  // một dòng KB trùng (dữ liệu cũ) — giữ để bộ sinh số lệch đúng như board
  const d0 = rows.find((x) => x.code === 'KB' && x.mi === 2 && x.st === 'pend' && x.roe);
  if (d0) { const day = Math.min(22, d0.day + 1); rows.push({ ...d0, id: 'r' + ++seq, day, date: String(day).padStart(2, '0') + '/Oct/2026' }); }
  const docs: Record<string, RfDoc> = {};
  let dn = 0;
  rows.filter((x) => x.gk).sort((a, b) => a.mi - b.mi).forEach((x) => {
    if (!docs[x.gk]) {
      dn++;
      docs[x.gk] = { doc: 'RFD26' + (70912 + dn * 37), code: x.code, partner: x.partner, collab: x.collab, mi: x.mi, per: x.per, date: ['05/Sep/2026', '06/Oct/2026', '21/Oct/2026'][x.mi]!, st: x.st, tpl: x.code === 'KB' ? 'tax' : dn % 2 ? 'roe' : 'tax' };
    }
    x.doc = docs[x.gk]!.doc;
  });
  // job trong kỳ chưa khai (đếm độc lập BOOKINGDATA): chỉ COM — vẫn gọi bộ sinh số để giữ đúng thứ tự như board
  const MISS = { KB: [0, 3, 14], COM: [1, 4, 11] };
  const miss: { code: RfCode; cus: string; car: string; mi: number }[] = [];
  (['KB', 'COM'] as const).forEach((code) => MON.forEach(([, , md], mi) => {
    for (let i = 0; i < MISS[code][mi]!; i++) { const k = pickW(KBP); const c = pickW(COMP); r(); void md; miss.push({ code, cus: c[0], car: k[0], mi }); }
  }));
  return { rows, docs: Object.values(docs), miss };
}

const DATA = build();
/** Ba kỳ trước (số job COM minh hoạ, tỷ trọng đối tác × tổng) — cột "6 kỳ". */
const PREV: [string, number][] = [['May', 17], ['Jun', 18], ['Jul', 20]];

export interface RfFilter { period: string; status: 'all' | RfBucket; q: string; roe: boolean; dbn: boolean; dup: boolean; coll: string }
export const RF_FILTER0: RfFilter = { period: '10', status: 'pend', q: '', roe: false, dbn: false, dup: false, coll: '' };

export interface RefundView {
  partner: string;
  /** dòng trong kỳ của khách (chưa lọc trạng thái) */
  inPeriod: RfRow[];
  /** dòng trong kỳ chưa lọc người nhận — danh sách người nhận */
  periodAll: RfRow[];
  rows: RfRow[];
  docs: RfDoc[];
  counts: Record<'all' | RfBucket, number>;
  jobs: number;
  openRows: number;
  roeGap: number;
  dbnGap: number;
  /** dòng KB trùng (cùng job, cùng người nhận) */
  dupGap: number;
  six: { k: string; n: number }[];
  /** số job trong kỳ chưa khai refund (minh hoạ theo board: 1, 4, 11) */
  missing: number;
  coverage: number;
}


/** Dòng KB trùng: cùng job, cùng người nhận có từ 2 dòng trở lên (kỳ của đối tác). */
const dupKey = (x: RfRow) => `${x.job}|${x.collab}`;
const isDup = (list: RfRow[]) => { const n = new Map<string, number>(); list.forEach((x) => n.set(dupKey(x), (n.get(dupKey(x)) ?? 0) + 1)); return (x: RfRow) => x.code === 'KB' && x.collab !== 'NIL' && (n.get(dupKey(x)) ?? 0) > 1; };

export function refundFor(partner: string, f: RfFilter, empty = false, code: RfCode = 'COM'): RefundView {
  const mine = empty ? [] : DATA.rows.filter((x) => x.code === code && x.partner === partner);
  const periodAll = mine.filter((x) => x.per === f.period);
  const inPeriod = periodAll.filter((x) => !f.coll || x.collab === f.coll);
  const q = f.q.trim().toLowerCase();
  const dup = isDup(inPeriod);
  const rows = inPeriod
    .filter((x) => (f.status === 'all' || bucketOf(x.st) === f.status)
      && (!q || `${x.job} ${x.partner} ${x.cus} ${x.sale} ${x.doc ?? ''}`.toLowerCase().includes(q))
      && (!f.roe || !x.roe) && (!f.dbn || x.dbn !== 'Done') && (!f.dup || dup(x)))
    .sort((a, b) => b.day - a.day || (a.job < b.job ? -1 : 1));
  const counts = { all: inPeriod.length, pend: 0, wait: 0, chi: 0, paid: 0, nil: 0 } as RefundView['counts'];
  inPeriod.forEach((x) => { counts[bucketOf(x.st)]++; });
  const docs = empty ? [] : DATA.docs.filter((d) => d.code === code && d.partner === partner && d.per === f.period);
  const mi = MON.findIndex((m) => m[0] === f.period);
  const jobs = new Set(inPeriod.map((x) => x.job)).size;
  const missing = empty ? 0 : DATA.miss.filter((m) => m.code === code && (code === 'KB' ? m.car : m.cus) === partner && m.mi === mi).length;
  const withRf = new Set(inPeriod.filter((x) => x.st !== 'nil' || x.collab === 'NIL').map((x) => x.job)).size;
  const total = withRf + missing;
  const cur = MON.map(([mm, mk]) => ({ k: mk, n: new Set(mine.filter((x) => x.per === mm && x.st !== 'nil').map((x) => x.job)).size }));
  return {
    partner, inPeriod, periodAll, rows, docs, counts, jobs,
    openRows: inPeriod.filter((x) => ['pend', 'wait', 'chi'].includes(bucketOf(x.st))).length,
    roeGap: inPeriod.filter((x) => !x.roe && x.st === 'pend').length,
    dbnGap: inPeriod.filter((x) => x.dbn !== 'Done' && x.st === 'pend').length,
    dupGap: inPeriod.filter(isDup(inPeriod)).length,
    six: empty ? [] : PREV.map(([k, n]) => ({ k, n })).concat(cur),
    missing,
    coverage: total ? withRf / total : 0,
  };
}

/** Khách có hồ sơ riêng trong dữ liệu mẫu refund; khách khác dùng đối tác GREENBUD như board (lock). */
export const refundPartner = (shortName: string) => (COMP.some(([c]) => c === shortName) ? shortName : 'GREENBUD');
export const rfDocLabel = (code: RfCode) => (code === 'COM' ? 'COM' : 'KB');

export const rfDupOf = (list: RfRow[]) => isDup(list);
